import * as React from 'react';
import { sendValuationRequestedEmail } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../context/Appointment/appointment';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { useLocale } from '../../context/Localization/localization';
import { useSetKioskBackgroundVisibility } from '../../context/Page/page';
import { Spinner } from '../../controls/Spinner/Spinner';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { DisplayMake, DisplayModel } from '../../helpers/displayVehicle';
import { ValuationExclusive } from './Content/ValuationExclusive';
import { ValuationFullScreen } from './Content/ValuationFullScreen';
import { ValuationGrid } from './Content/ValuationGrid';
import { Locales } from '../../context/Localization/localization.types';
import { MobileValuationScreen, ValuationScreen } from '../../API/Enums';
import { GoMotoFunction } from '../../API/GoMoto.types';

export interface IProps {
    /** User proceeded through the screen */
    onProceed: () => void;
    /** The screen was not displayed to the user */
    onSkip: () => void;
}
interface IVehicleRule {
    yearMin: number;
    yearMax: number;
    make: string;
    model: string;
}

type ValuationPageType = 'fullscreen' | 'exclusive' | 'normal';

export const Valuation: React.FC<IProps> = React.memo((props) => {
    const appointment = useAppointmentState();
    const locale = useLocale();
    const setAppointment = useAppointmentUpdateAndPersist();
    const setBackgroundVisibility = useSetKioskBackgroundVisibility();
    const currentYear = new Date()
        .getFullYear();
    const apptYear = parseInt(appointment.year ?? '0', 10) || 0;
    const {
        dealer: { exclusiveValuationPage, vehicleRuleSetting, valuationFullScreen: valuationFullScreenEnabled, valuationFullScreenImage, valuationScreen, mobileValuationScreen },
    } = useKioskState();
    let pageType: ValuationPageType = 'normal';

    const hasMatch = React.useMemo<boolean>(() => {
        /**
         * Parses a `vehicleRuleSetting` string into an array of IVehicleRule entries.
         *
         * Two formats are supported:
         *  - New (written by Support Dashboard): `"Make1, Make2|YearMin-YearMax"`,
         *    `"YearMin-YearMax"`, or `"Make1, Make2"` (manufacturers-only falls
         *    through to the legacy parser — both produce equivalent per-make rules).
         *  - Legacy (written by the legacy Admin UI): comma-separated rules, each
         *    of the form `"YearMin-YearMax Make Model"`.
         */
        const parseVehicleRuleSetting: GoMotoFunction<[string], IVehicleRule[]> = (setting) => {
            if (!setting) {
                return [];
            }

            const pipeIndex: number = setting.indexOf('|');
            if (pipeIndex >= 0) {
                const makesPart: string = setting.slice(0, pipeIndex)
                    .trim();
                const yearPart: string = setting.slice(pipeIndex + 1)
                    .trim();
                const yearMatch: RegExpMatchArray | null = yearPart.match(/^(\d{4})\s*-\s*(\d{4})$/);
                if (yearMatch) {
                    const parsedYearMin: number = parseInt(yearMatch[1], 10);
                    const parsedYearMax: number = parseInt(yearMatch[2], 10);
                    const makes: string[] = makesPart.split(/\s*,\s*/)
                        .filter((m) => !!m);
                    return makes.length > 0
                        ? makes.map((make) => ({
                            yearMin: parsedYearMin,
                            yearMax: parsedYearMax,
                            make,
                            model: '',
                        }))
                        : [{
                            yearMin: parsedYearMin,
                            yearMax: parsedYearMax,
                            make: '',
                            model: '',
                        }];
                }
            }

            const yearRangeOnlyMatch: RegExpMatchArray | null = setting.match(/^(\d{4})\s*-\s*(\d{4})$/);
            if (yearRangeOnlyMatch) {
                return [{
                    yearMin: parseInt(yearRangeOnlyMatch[1], 10),
                    yearMax: parseInt(yearRangeOnlyMatch[2], 10),
                    make: '',
                    model: '',
                }];
            }

            return setting.split(/\s*,\s*/)
                .filter((i) => !!i)
                .map((i) => {
                    const [, yearAStr, yearBStr, make, model] = i.trim()
                        .match(/(\d*)\s*-?\s*(\d*)\s*(\S*)\s*(.*)/) || [];
                    const yearA: number = parseInt(yearAStr, 10) || 0;
                    const yearB: number = parseInt(yearBStr, 10) || yearA;
                    const ruleYearMax: number = Math.max(yearA, yearB);
                    return {
                        yearMin: Math.min(yearA, yearB, currentYear),
                        yearMax: ruleYearMax || Infinity,
                        make: make,
                        model: model,
                    };
                });
        };

        const matchArray: IVehicleRule[] = parseVehicleRuleSetting((vehicleRuleSetting ?? '').trim());

        // If no `vehicleRuleSetting`, fall back to matching on vehicles at or older than the current year
        return matchArray.length > 0 ? !!matchArray.find((m) =>
            m.yearMin <= apptYear && m.yearMax >= apptYear &&
            (!m.make || new RegExp(m.make.toLowerCase())
                .test(DisplayMake(appointment)
                    .toLowerCase())) &&
            (!m.model || DisplayModel(appointment)
                .toLowerCase()
                .startsWith(m.model.toLowerCase())),
        ) : apptYear <= currentYear;
    }, [appointment, vehicleRuleSetting, currentYear, apptYear]);

    const isImageOption: boolean =
        valuationScreen === ValuationScreen.ValueSplash_BMWHONOLULU ||
        valuationScreen === ValuationScreen.ValueSplash_MINIHAWAII;

    /*
     * Valuation page is adjusted based on mobile/kiosk.
     * We do not use the fullscreen valuation in mobile, because of the variable sizes of devices conflicting with our statically sized full screen valuation image
     * We do not use the fullscreen valuation for non-english pages because text on the images are specific to english
     */
    if (isElectron() && valuationFullScreenEnabled && !!valuationFullScreenImage && locale === Locales.en) {
        pageType = 'fullscreen';
    }
    else if (!isElectron() && isImageOption && mobileValuationScreen === MobileValuationScreen.Blackbook) {
        // Show Blackbook prices on mobile instead of image
        pageType = 'normal';
    }
    else if (exclusiveValuationPage) {
        pageType = 'exclusive';
    }

    const reviewEquity = async (review: boolean) => {
        const updatedAppt: typeof appointment = {
            ...appointment,
            tradeValuationRequested: review,
        };

        if (valuationFullScreenEnabled) {
            updatedAppt.valuationFullScreenImage = valuationFullScreenImage;
        }

        await setAppointment(updatedAppt);
        review && sendValuationRequestedEmail(updatedAppt.key, updatedAppt.dealerKey);

        props.onProceed();
    };

    const { onSkip } = props;

    React.useEffect(() => {
        console.log('Checking to make sure we don\'t offer valuations to relatively new cars');
        console.log(`Customer's vehicle's year: ${apptYear}`);
        console.log(`Year cut off: ${currentYear}`);

        if (!hasMatch) {
            onSkip();
        }
    }, [hasMatch, onSkip, setAppointment, apptYear, currentYear]);

    if (!hasMatch) {
        return <Spinner />;
    }
    else {
        switch (pageType) {
            case 'fullscreen':
                setBackgroundVisibility(false);

                return <ValuationFullScreen onReviewEquity={reviewEquity} />;
            case 'exclusive':
                setBackgroundVisibility(true);

                return <ValuationExclusive onReviewEquity={reviewEquity} />;
            case 'normal':
                setBackgroundVisibility(true);

                return <ValuationGrid onReviewEquity={reviewEquity} onSkip={onSkip} />;
        }
    }
});
